/**
 * MODULE: domain/pages/frontend/utils/pageCommentTaskUtils.ts
 * PURPOSE: Build CRM task payloads when converting page comments to tasks
 */

import type { CreateTaskRequest } from '@shared/lib/frontend/api/taskApi';
import type { PageComment } from '../../contracts/pages.types';
import { parseCommentContent } from './commentMentionUtils';
import { buildPageTaskSyncTag } from './pageTasksSyncUtils';

export const PAGE_COMMENT_TASK_TAG_PREFIX = 'pages:comment:';

export function buildPageCommentTaskTag(commentId: string): string {
  return `${PAGE_COMMENT_TASK_TAG_PREFIX}${commentId}`;
}

export function commentPlainText(content: string): string {
  return parseCommentContent(content)
    .map((part) => (part.type === 'mention' ? `@${part.label}` : part.value))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

export function commentContentToTaskTitle(content: string, maxLength = 80): string {
  const plain = commentPlainText(content);
  if (!plain) {
    return 'Follow up from page comment';
  }
  const firstLine = plain.split('\n')[0]?.trim() ?? plain;
  if (firstLine.length <= maxLength) {
    return firstLine;
  }
  return `${firstLine.slice(0, maxLength - 1).trim()}…`;
}

export function buildCommentToTaskRequest(
  pageId: string,
  workspaceId: string,
  comment: PageComment
): CreateTaskRequest {
  const plain = commentPlainText(comment.content);
  return {
    title: commentContentToTaskTitle(comment.content),
    description: plain || undefined,
    status: 'todo',
    priority: 'medium',
    tags: [buildPageTaskSyncTag(pageId), buildPageCommentTaskTag(comment.id)],
    ai_context: `Converted from page comment ${comment.id} on page ${pageId} (workspace ${workspaceId})`,
  };
}

export function taskLinkedToComment(tags: string[], commentId: string): boolean {
  return tags.includes(buildPageCommentTaskTag(commentId));
}

export function extractCommentIdFromTaskTags(tags: string[]): string | null {
  const tag = tags.find((entry) => entry.startsWith(PAGE_COMMENT_TASK_TAG_PREFIX));
  if (!tag) {
    return null;
  }
  return tag.slice(PAGE_COMMENT_TASK_TAG_PREFIX.length) || null;
}
